import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import TreeHealthDialog from './TreeHealthDialog';
import {
  Card,
  CardContent,
  CardActions,
  Fade,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormHelperText,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import LinkIcon from '@mui/icons-material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useForm, Controller } from 'react-hook-form';
import FamilyMemberTable from './FamilyTable';
import { transformDate } from '../../../utils/date';
import { capitalise } from '../../../utils/capitalise';
import { UpdateTreePayload } from '../../../types/tree';
import { useAuth } from '../../../components/auth/AuthProvider';
import {
  freemiumTreeCollaborators,
  textLimits,
  maxCollaborators,
} from '../../../core/constants/constants';
import { isTreeOwner } from './utils';
import useIsAdmin from '../../../hooks/useIsAdmin';
import useSubscription from '../../../hooks/useSubscription';
import { useModal } from '../../../components';
import SubscribeModal from '../../../components/common/SubscribeModal';
import {
  getCharacterCountHelperText,
  hasReachedCharacterLimit,
  sanitiseNameInput,
  sanitiseTextInput,
} from '../../../utils';

type TreeOverviewProps = {
  initialData?: any;
  onSave?: any;
  openDeleteTreeModal: () => void;
  openAddCollaboratorsModal: () => void;
  navToAddMember?: () => void;
  handleGedcomExport?: () => void;
};

export default function FamilyTreeOverview({
  initialData,
  onSave,
  openDeleteTreeModal,
  openAddCollaboratorsModal,
  navToAddMember,
  handleGedcomExport,
}: TreeOverviewProps) {
  const { breakpoints } = useTheme();
  const { openModal, closeModal } = useModal();
  const { control, handleSubmit, setValue, watch, reset, formState } = useForm({
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      is_public: initialData?.is_public || false,
      collaborators: initialData?.collaborators || [],
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEdit = () => {
    reset({
      name: initialData.name,
      description: initialData.description,
      is_public: initialData.is_public,
      collaborators: initialData.collaborators || [],
    });
    setIsEditing((prev) => !prev);
  };
  const { currentUser } = useAuth();
  const { isAdmin } = useIsAdmin(currentUser);
  const { isPremium } = useSubscription();

  const isPaidUser = isPremium || isAdmin;
  const canCheckHealth =
    Boolean(initialData?.id) &&
    (isTreeOwner(initialData, currentUser) ||
      Boolean(
        currentUser?.email &&
          initialData?.collaborators?.some(
            (collaborator: string) =>
              collaborator.toLowerCase() === currentUser.email?.toLowerCase(),
          ),
      ));
  const handleSave = (data: any) => {
    if (!currentUser) {
      return;
    }
    const payload: UpdateTreePayload = {
      name: sanitiseNameInput(data.name),
      description: sanitiseTextInput(data.description),
      collaborators: data.collaborators,
      is_public: data.is_public,
      updated_by: currentUser.uid,
    };
    onSave(payload);
    setIsEditing(false);
  };

  const data = initialData?.members?.map((member: any) => ({
    first_name: member.first_name ? member.first_name : 'No first name',
    last_name: member.last_name ? member.last_name : 'No last name',
    gender: capitalise(member.gender),
    date_of_birth: transformDate(member.date_of_birth),
    last_name_at_birth: member.last_name_at_birth
      ? member.last_name_at_birth
      : undefined,
  }));

  const collaborators = watch('collaborators');
  const isLimitReached = collaborators.length >= freemiumTreeCollaborators;
  const disableAddCollaborator = collaborators.length >= maxCollaborators;
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isDirty = formState.isDirty;
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddCollaborator = () => {
    if (isLimitReached && !isPaidUser) {
      return openModal(
        <SubscribeModal
          closeModal={closeModal}
          handleClose={closeModal}
          message={`Upgrade your plan to add more than ${freemiumTreeCollaborators} collaborators.`}
          title="Collaborate with family member"
        />,
      );
    }
    openAddCollaboratorsModal();
  };

  return (
    <Box sx={{ width: '100%', my: 2, pb: 2 }}>
      <Fade in={isEditing} timeout={220} mountOnEnter unmountOnExit>
        <Card
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            mt: 1,
            borderColor: 'divider',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
          }}
          variant="outlined"
        >
          <CardContent>
            <form onSubmit={handleSubmit(handleSave)}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Edit Tree Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update the profile and sharing settings for your family
                    tree.
                  </Typography>
                </Box>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      placeholder="e.g. The Okafor Family Tree"
                      inputProps={{ maxLength: textLimits.treeName }}
                    />
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      multiline
                      rows={5}
                      placeholder="Share the story behind this tree, key branches, and context for collaborators."
                      inputProps={{ maxLength: textLimits.treeDescription }}
                      error={hasReachedCharacterLimit(
                        field.value,
                        textLimits.treeDescription,
                      )}
                      helperText={getCharacterCountHelperText(
                        field.value,
                        textLimits.treeDescription,
                      )}
                    />
                  )}
                />
                <Controller
                  name="collaborators"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="collaborators-label">
                        Collaborators
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="collaborators-label"
                        multiple
                        input={<OutlinedInput label="Collaborators" />}
                        renderValue={(selected) => {
                          if (!selected || selected.length === 0) {
                            return 'No collaborators selected';
                          }
                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {(selected as string[]).map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          );
                        }}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        {collaborators.map((email: string) => (
                          <MenuItem key={email} value={email}>
                            <Checkbox checked={field.value?.includes(email)} />
                            <ListItemText primary={email} />
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select collaborators linked to this tree.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
                <Controller
                  name="is_public"
                  control={control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography fontWeight={600} variant="body2">
                          Public Tree
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Allow anyone can view this tree.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Switch
                            {...field}
                            checked={field.value}
                            onChange={(e) =>
                              setValue('is_public', e.target.checked)
                            }
                          />
                        }
                        label=""
                      />
                    </Box>
                  )}
                />
              </Stack>
            </form>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end', gap: 1.25, px: 2 }}>
            <Button onClick={handleEdit} color="primary" variant="text">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(handleSave)}
              variant="contained"
              color="primary"
              disableElevation
              disabled={!isDirty}
            >
              Save
            </Button>
          </CardActions>
        </Card>
      </Fade>
      {!isEditing && (
        <Stack spacing={2}>
          <Paper
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                Tree Details
              </Typography>
              {isMobile && isTreeOwner(initialData, currentUser) && (
                <div>
                  <IconButton
                    aria-label="more"
                    id="long-button"
                    aria-controls={open ? 'long-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'transform 180ms ease, box-shadow 180ms ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <MoreVertIcon color="primary" />
                  </IconButton>
                  <Menu
                    id="long-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                  >
                    <MenuItem
                      onClick={() => {
                        setIsEditing(true);
                        handleClose();
                      }}
                    >
                      Edit
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        if (navToAddMember) {
                          navToAddMember();
                        }
                        handleClose();
                      }}
                    >
                      Add member
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleAddCollaborator();
                        handleClose();
                      }}
                      disabled={disableAddCollaborator}
                    >
                      Add collaborator
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleCopy();
                        handleClose();
                      }}
                    >
                      {copied ? 'Tree copied' : 'Copy tree URL'}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        openDeleteTreeModal();
                        handleClose();
                      }}
                    >
                      Delete
                    </MenuItem>
                  </Menu>
                </div>
              )}
              {!isMobile && isTreeOwner(initialData, currentUser) ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.25,
                    flexWrap: 'wrap',
                  }}
                >
                  <Tooltip title="Edit" arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={() => setIsEditing(true)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          transition:
                            'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" color="primary" />
                      </IconButton>
                      <Typography variant="caption">Edit</Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Add member" arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          if (navToAddMember) {
                            navToAddMember();
                          }
                        }}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          transition:
                            'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <PersonAddIcon fontSize="small" color="primary" />
                      </IconButton>
                      <Typography variant="caption">Add member</Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Add collaborator" arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={handleAddCollaborator}
                        disabled={disableAddCollaborator}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          transition:
                            'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <PersonAddIcon fontSize="small" color="primary" />
                      </IconButton>
                      <Typography variant="caption">
                        Add collaborator
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Copy tree URL" arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={handleCopy}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          transition:
                            'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <LinkIcon fontSize="small" color="primary" />
                      </IconButton>
                      <Typography variant="caption">
                        {copied ? 'Tree copied' : 'Copy tree URL'}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={openDeleteTreeModal}
                        sx={{
                          border: '1px solid',
                          borderColor: 'error.light',
                          backgroundColor: 'background.paper',
                          transition:
                            'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                            backgroundColor: 'error.lighter',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                      <Typography variant="caption">Delete</Typography>
                    </Box>
                  </Tooltip>
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                background:
                  'linear-gradient(180deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.01) 100%)',
              }}
            >
              <Chip
                label={initialData?.is_public ? 'Public tree' : 'Private tree'}
                size="small"
                color="info"
                sx={{ mb: 1 }}
              />
              <Typography variant="subtitle1" fontWeight={600}>
                {initialData?.name || 'Untitled family tree'}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {initialData?.description || 'No description yet.'}
              </Typography>
            </Box>
          </Paper>
        </Stack>
      )}

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, mt: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Family Members{' '}
            {initialData?.members?.length > 0
              ? `(${initialData?.members?.length})`
              : null}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {canCheckHealth && (
              <Button
                variant="outlined"
                startIcon={<HealthAndSafetyIcon />}
                onClick={() =>
                  openModal(
                    <TreeHealthDialog
                      treeId={initialData.id}
                      isPaidUser={isPaidUser}
                      closeModal={closeModal}
                    />,
                  )
                }
              >
                Check tree health
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadForOfflineIcon />}
              onClick={
                !isPaidUser
                  ? () =>
                      openModal(
                        <SubscribeModal
                          closeModal={closeModal}
                          handleClose={closeModal}
                          message={
                            'Download GEDCOM data for your family tree is unavailable on the free plan.'
                          }
                          title="Download Your Family Tree and Share with your family members"
                        />,
                      )
                  : handleGedcomExport
              }
            >
              Export GEDCOM
            </Button>
          </Box>
        </Box>
        <Box pt={1.5}>
          {data?.length > 0 ? (
            <FamilyMemberTable data={data} />
          ) : (
            <Typography color="text.secondary">
              No family members yet.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
